<template>
  <div
      v-if="group?.events?.length"
      id="abacus-container"
      class="container-fluid"
  >
    <div class="row">
      <div class="table-responsive ps-0">
        <table class="table table-bordered table-sm">
          <thead>
          <tr v-if="group.training.type === TrainingType.Recurring">
            <th scope="col" colspan="2"/>
            <th scope="col" v-for="event in group.events" :key="event.id">
              {{ dateFormat(event.startTime) }}
            </th>
            <th scope="col" v-if="showTransferred">
              {{ t("TRANSFERRED") }}
            </th>
            <th scope="col" colspan="2">
              {{ t("TOTAL") }}
            </th>
          </tr>
          <tr>
            <th scope="col" v-for="f in tableHeaders" :class="f.thClass?.()">{{ f.label }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="trainee in traineesWithEvents" v-if="traineesWithEvents.length">
            <td class="background-blue">{{ trainee.name }}</td>

            <td>
              <router-link :to="{ name: 'Trainee', params: { id: trainee.accountId } }">
                {{ trainee.ssn }}
              </router-link>
            </td>

            <template v-for="event in trainee.events">
              <template v-if="group.training.type === TrainingType.Recurring">
                <td>
                  <span v-if="event.isTransferred && !isArchived">{{ event.hoursPresent || "0" }}</span>
                  <input
                      v-else
                      v-model="event.hoursPresent"
                      placeholder="-"
                      class="attendance-input"
                      type="text"
                      @keydown="formHasBeenChanged = true"
                  />
                </td>
              </template>
              <td v-else>
                <Checkbox
                    v-model="event.isPresent"
                    :disabled="isArchived"
                    :binary="true"
                    @change="formHasBeenChanged = true"
                />
              </td>
            </template>

            <template v-if="group.training.type === TrainingType.Recurring">
              <td>{{ trainee.hoursTotal }}</td>
            </template>

            <td v-if="trainee.passed" class="text-success">{{ t("PASSED") }}</td>
            <td v-else class="text-danger">{{ t("NOT_PASSED") }}</td>
          </tr>

          <tr v-else>
            <td
                :colspan="group.training.type === TrainingType.Recurring
                  ? 4 + (showTransferred ? 1 : 0) + group.events.length
                  : 3 + (showTransferred ? 1 : 0) + group.events.length"
            >
              {{ t("NO_RESULTS") }}
            </td>
          </tr>
          </tbody>

          <caption v-if="group.training.type === TrainingType.Recurring">
            <small>* {{ AttendanceStatus.AbsentNotifiedShort }} - {{ t("TRAINEE_NOTIFIED_ABSENT") }}</small><br/>
            <small>* {{ AttendanceStatus.InterruptedNotifiedShort }} - {{ t("INTERRUPTED_NOTIFIED") }}</small>
          </caption>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col text-end">
        <Button
            v-if="isAdminOrSchoolManagerOrTrainer()"
            :disabled="!formHasBeenChanged || isArchived || loading || auth.isViewerLike()"
            :label="t('SAVE')"
            class="blue"
            @click="submitAttendance"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import {computed, onMounted, ref} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {useToast} from "@/util/toastUtils";
import {TrainingType} from "@/model/training.js";
import {AttendanceStatus} from "@/model/attendance.js";
import {Role} from "@/auth/oauth.js";
import {GroupStatus} from "@/model/group-status.js";
import {injectAuth, injectAxios} from "@/util/inject";

interface AbacusAttendance {
  accountId: string,
  status: string,
  hoursPresent: string,
  trainingEvent: null | {
    id: string,
    startTime: string,
  },
}

interface TraineeEvent {
  eventId?: string,
  isTransferred: boolean,
  hoursPresent: string,
  isPresent: boolean,
}

interface TraineeWithEvents {
  accountId: string,
  name: string,
  ssn: string,
  passed: boolean,
  hoursPresent: number,
  hoursTotal: number,
  events: TraineeEvent[],
}

const {dateFormat} = useDateFormat();
const axios = injectAxios();
const route = useRoute();
const {t} = useI18n();
const toast = useToast();
const auth = injectAuth();
const formHasBeenChanged = ref(false);
const attendances = ref<AbacusAttendance[]>([]);
const loading = ref(false);
const props = defineProps(["group"]);

onMounted(async () => {
  await getAttendances();
});

const isArchived = computed(() => {
  return props.group.status === GroupStatus.ARCHIVED;
});

function isAdminOrSchoolManagerOrTrainer() {
  return auth.hasRole(Role.ADMIN) || auth.hasRole(Role.SCHOOL_MANAGER) || auth.hasRole(Role.TRAINER);
}

async function getAttendances() {
  const resp = await axios.get(`/attendance/group`, {params: {groupId: route.params.id}});
  attendances.value = resp?.data;
}

async function submitAttendance() {
  loading.value = true;

  try {
    await axios.post("/attendance/group/" + props.group.id, createAttendanceRequestBody());
    await getAttendances();
  } finally {
    loading.value = false;
  }

  toast.makeSuccessToast(t("SAVED"));
  formHasBeenChanged.value = false;
}

const eventsMergedWithAttendances = computed(() => {
  const result: AbacusAttendance[] = [];
  const trainees = props.group?.trainees ?? [];
  const events = props.group?.events ?? [];

  for (const {trainee} of trainees) {
    for (const ev of events) {
      const att = attendances.value.find((att) => att.accountId === trainee.id && ev.id === att.trainingEvent?.id);
      result.push(att ?? {
        accountId: trainee.id,
        status: "",
        hoursPresent: "",
        trainingEvent: ev,
      });
    }

    const transferredAtt = attendances.value.find((att) => att.accountId === trainee.id && !att.trainingEvent);
    result.push(transferredAtt ?? {
      accountId: trainee.id,
      status: "",
      hoursPresent: "",
      trainingEvent: null,
    });
  }

  return result;
});

const showTransferred = computed(() => {
  return eventsMergedWithAttendances.value.some(att => !att.trainingEvent && att.hoursPresent);
});

const tableHeaders = computed(() => {
  const fields: { label: string, thClass?: () => string }[] = [
    {label: t("NAME"), thClass: () => "text-start"},
    {label: t("SSN"), thClass: () => "text-start"},
  ];

  for (let i = (showTransferred.value ? 0 : 1); i <= props.group.events.length; i++) {
    fields.push({label: t("PRESENT")});
  }

  if (props.group.training?.type === TrainingType.Recurring) {
    fields.push({label: t("HOURS")});
  }

  fields.push({label: t("STATUS")});
  return fields;
});

const traineesWithEvents = computed(() => {
  if (!props.group || !props.group.trainees) {
    return [];
  }

  const trainees: TraineeWithEvents[] = props.group.trainees.map(({trainee}) => {
    return {
      accountId: trainee.id,
      name: trainee.fullName,
      ssn: trainee.ssn,
      passed: false,
      hoursPresent: 0,
      hoursTotal: 0,
      events: [],
    };
  });

  // Build event list for trainee.
  for (const attendance of eventsMergedWithAttendances.value) {
    if (!showTransferred.value && !attendance.trainingEvent) {
      continue;
    }

    const trainee = trainees.find(t => t.accountId === attendance.accountId);
    if (!trainee) {
      continue;
    }

    const event: TraineeEvent = {
      eventId: attendance.trainingEvent?.id,
      isTransferred: !attendance.trainingEvent,
      isPresent: attendance.status === AttendanceStatus.Present,
      hoursPresent: "",
    };

    if (attendance.status === AttendanceStatus.Present) {
      trainee.hoursPresent += Number.parseInt(attendance.hoursPresent) || 0;
      event.hoursPresent = String(attendance.hoursPresent || 0);
    } else if (attendance.status === AttendanceStatus.AbsentNotified) {
      event.hoursPresent = AttendanceStatus.AbsentNotifiedShort;
    } else if (attendance.status === AttendanceStatus.InterruptedNotified) {
      event.hoursPresent = AttendanceStatus.InterruptedNotifiedShort;
    }

    trainee.events.push(event);
  }

  for (const trainee of trainees) {
    trainee.hoursTotal = trainee.hoursPresent;
    trainee.passed = hasPassedTraining(trainee);
  }

  return trainees;
});

const hasPassedTraining = (trainee: TraineeWithEvents) => {
  if (props.group.training.type === TrainingType.Recurring) {
    return trainee.hoursTotal >= props.group.lessons;
  }

  return trainee.events.some(ev => ev.isPresent);
};

const eventToStatus = (ev: TraineeEvent) => {
  if (props.group.training.type === TrainingType.Recurring) {
    if (ev.hoursPresent) {
      if (ev.hoursPresent.toUpperCase() === AttendanceStatus.AbsentNotifiedShort) {
        return AttendanceStatus.AbsentNotified;
      }

      if (ev.hoursPresent.toUpperCase() === AttendanceStatus.InterruptedNotifiedShort) {
        return AttendanceStatus.InterruptedNotified;
      }

      if (Number.parseInt(ev.hoursPresent)) {
        return AttendanceStatus.Present;
      }
    }

    return AttendanceStatus.Absent;
  }

  return ev.isPresent ? AttendanceStatus.Present : AttendanceStatus.Absent;
};

const createAttendanceRequestBody = () => {
  const requestBody = [];

  for (const att of traineesWithEvents.value) {
    for (const ev of att.events) {
      // Skip transferred events.
      if (ev.eventId) {
        requestBody.push({
          trainingEventId: ev.eventId,
          accountId: att.accountId,
          hoursPresent: Number.parseInt(ev.hoursPresent) || 0,
          status: eventToStatus(ev),
        });
      }
    }
  }

  return requestBody;
};
</script>

<style lang="scss" scoped>
@import "@/scss/custom.scss";

.attendance-input {
  border: none;
  border-bottom: 1px solid var(--p-color-white-200);
  background: transparent;
  outline: none;
  width: 40px;
  text-align: center;
  margin-bottom: 0.125rem;
}

#abacus-container ::v-deep(.background-blue) {
  background-color: var(--p-color-blue-500);
  color: var(--p-color-white-0);
  position: sticky;
  left: 0;
  z-index: 1;
  max-width: 33vw;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(td:nth-child(n + 3)),
:deep(th) {
  text-align: center;
}

#abacus-container :deep(th) {
  font-size: 14px;
  border-bottom-width: 1px;
  color: var(--p-color-blue-600);
}

#abacus-container :deep(td),
#abacus-container :deep(th) {
  white-space: nowrap;
  padding: 0.25rem 0.5rem;
}
</style>
